import { Injectable } from '@angular/core';
import { PolygonAPIService } from './polygon-api.service';
import { UtilityService } from '../util/utility.service';
import { environment } from '@environment';

interface Coordinate {
	lat: number;
	lng: number;
}

interface PolygonData {
	polygon_id: string;
	name: string;
	coordinates_list: Coordinate[];
}

interface ColorConfig {
	background: string;
	border: string;
	strokeWidth: number;
}

class PolygonColors {

	static default: ColorConfig = {
		background: "rgba(96, 96, 96, 0.30)",
		border: "#606060",
		strokeWidth: 1
	};

	static colors: Record<number, { selected: ColorConfig }> = {
		[1]: {
			selected: {
				background: "rgba(237, 64, 75, 0.30)",
				border: "#ED404B",
				strokeWidth: 2
			}
		},
		[13]: {
			selected: {
				background: "rgba(244, 120, 29, 0.30)",
				border: "#F4781D",
				strokeWidth: 2
			}
		},
		[19]: {
			selected: {
				background: "rgba(34, 219, 219, 0.30)",
				border: "#1daaaa",
				strokeWidth: 2
			}
		}
	}

	static getColors(): { 
		selected: ColorConfig; 
		default: ColorConfig 
	} {
		return {
			selected: (this.colors[environment.brand.id] || this.colors[19]).selected,
			default: PolygonColors.default
		};
	}
}

export class PolygonInfo {

	#id: string;
	#name: string;
	#gPolygon: google.maps.Polygon;
	#infoWindow: google.maps.InfoWindow;
	#paths: google.maps.LatLng[];
	dir: string

	constructor(polygon: PolygonData) {
		this.#setName(polygon.name);
		this.#setId(polygon.polygon_id);
		this.#setPaths(polygon.coordinates_list);
		this.#createPolygon(polygon);
		this.#createInfoWindow(polygon);
	}

	#setPaths(coordinates: Coordinate[]): void {
		this.#paths = coordinates.map((coordinate: Coordinate) =>
			new google.maps.LatLng(coordinate.lat, coordinate.lng)
		);
	}

	getPolygon(): google.maps.Polygon {
		return this.#gPolygon;
	}

	getInfoWindow(): google.maps.InfoWindow {
		return this.#infoWindow;
	}

	getName(): string {
		return this.#name;
	}

	getId(): string {
		return this.#id;
	}

	highlight(is_inside: boolean = false): void {
		this.#setPolygonUI(is_inside);
	}

	#createPolygon(polygon: PolygonData): void {
		this.#gPolygon = new google.maps.Polygon({
			paths: polygon.coordinates_list
		});
		this.#setPolygonUI(false);
	}

	#setPolygonUI(is_inside: boolean): void {
		const color = is_inside ? PolygonColors.getColors().selected : PolygonColors.getColors().default;
		this.#gPolygon.setOptions({
			fillColor: color.background,
			strokeColor: color.border,
			strokeWeight: color.strokeWidth
		});
	}

	#createInfoWindow(polygon: PolygonData): void {
		const center = this.#getCenter(polygon.coordinates_list);
		const info = `<div class="poi-name"> ${polygon.name} <div>`;

		if (!center) return;

		this.#infoWindow = new google.maps.InfoWindow({
			content: info,
			position: {
				lat: center.lat(),
				lng: center.lng()
			},
			disableAutoPan: true,
		});
	}

	updateInfoWindowPosition(lat: number, prev = null): void {

		if (!this.#infoWindow) {
			return;
		}

		const prev_center = prev?.getPolygon().map.getCenter();
		if (!prev_center) {
			this.dir = this.#infoWindow.getPosition().lat() < lat ? 'top-left' : 'top-right';
		} else {

			let cx = prev_center.lat() - this.#infoWindow.getPosition().lat();
			let cy = prev_center.lng() - this.#infoWindow.getPosition().lng();

			let dir = prev.dir;

			if (Math.abs(cy) < 0.0004 || Math.abs(cx) < 0.0004) {
				dir = dir === 'top-left' ? 'top-right' : 'top-left';
			}
			this.dir = dir;				
		}

		const content = `<div class="poi-name ${this.dir} ${this.#id}"> ${this.getName()} </div>`;
		this.#infoWindow.setContent(content);
	}

	#setName(name: string): void {
		this.#name = name;
	}

	#setId(id: string): void {
		this.#id = id;
	}

	#getCenter(coordinates: Coordinate[]): google.maps.LatLng | null {

		if (!coordinates || coordinates.length === 0) {
			return null;
		}

		const bounds = new google.maps.LatLngBounds();
		coordinates.forEach(coordinate =>
			bounds.extend(new google.maps.LatLng(coordinate.lat, coordinate.lng))
		);
		return bounds.getCenter();
	}

	getPaths(): google.maps.Polygon {
		return new google.maps.Polygon({ paths: this.#paths });
	}
}

interface PolygonResponse {
	meta: {
		code: number;
	};
	result: PolygonData[];
}

@Injectable({
	providedIn: 'root'
})
export class PolygonService {
	#prevLatLng: Coordinate | null = null;

	constructor(
		private polygonAPI: PolygonAPIService,
		private utility: UtilityService
	) { }

	async getPolygons(lat: number, lng: number): Promise<PolygonData[] | null> {
		if (!this.#shouldFetchNewData(lat, lng)) {
			return null;
		}

		try {
			const response: PolygonResponse = await this.polygonAPI.fetchPolygons(lat, lng);
			if (response.meta.code === 200) {
				const polygons = this.#removeDuplicate(response.result);
				this.#prevLatLng = { lat, lng };
				return polygons;
			}
			return [];
		} catch (error) {
			console.error('Error fetching polygons:', error);
			return [];
		}
	}

	#shouldFetchNewData(lat: number, lng: number): boolean {
		if (this.#prevLatLng) {
			const distance = this.utility.getDistanceFromLatLonInMeter(
				lat, lng, this.#prevLatLng.lat, this.#prevLatLng.lng
			);
			if (distance < PolygonAPIService.MIN_DISTANCE) {
				return false;
			}
		}
		return true;
	}

	#removeDuplicate(polygons: PolygonData[]): PolygonData[] {
		const uniqueSet = new Set<string>();
		return polygons.filter(polygon => {
			const value = polygon.name.toLowerCase();
			if (uniqueSet.has(value)) {
				return false;
			}
			uniqueSet.add(value);
			return true;
		});
	}

	clearCache(): void {
		this.#prevLatLng = null;
	}
}